import * as THREE from 'three';
import { assetUrl } from './assetUrl';

const loader = new THREE.TextureLoader();
const anisotropy = 8;

export type PbrMaps = {
  map: THREE.Texture;
  normalMap?: THREE.Texture;
  roughnessMap?: THREE.Texture;
  aoMap?: THREE.Texture;
};

function configureMap(tex: THREE.Texture, color = false): THREE.Texture {
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.anisotropy = anisotropy;
  tex.needsUpdate = true;
  return tex;
}

function loadOptional(url: string, color = false): THREE.Texture | undefined {
  try {
    return configureMap(loader.load(url), color);
  } catch {
    return undefined;
  }
}

export function loadPbr(basePath: string, prefix: string): PbrMaps {
  const map = configureMap(loader.load(`${basePath}/${prefix}_Color.jpg`), true);
  const normalMap = loadOptional(`${basePath}/${prefix}_NormalGL.jpg`);
  const roughnessMap = loadOptional(`${basePath}/${prefix}_Roughness.jpg`);
  const aoMap = loadOptional(`${basePath}/${prefix}_AmbientOcclusion.jpg`);
  return { map, normalMap, roughnessMap, aoMap };
}

export function makePbrMaterial(
  maps: PbrMaps,
  opts: {
    color?: number;
    roughness?: number;
    metalness?: number;
    normalScale?: number;
    repeat?: number;
    emissive?: number;
    emissiveIntensity?: number;
  } = {},
): THREE.MeshStandardMaterial {
  const repeat = opts.repeat ?? 1;
  const applyRepeat = (t?: THREE.Texture) => {
    if (!t) return;
    t.repeat.set(repeat, repeat);
  };
  applyRepeat(maps.map);
  applyRepeat(maps.normalMap);
  applyRepeat(maps.roughnessMap);
  applyRepeat(maps.aoMap);

  return new THREE.MeshStandardMaterial({
    map: maps.map,
    normalMap: maps.normalMap,
    roughnessMap: maps.roughnessMap,
    aoMap: maps.aoMap,
    color: opts.color ?? 0xffffff,
    roughness: opts.roughness ?? 1,
    metalness: opts.metalness ?? 0.04,
    normalScale: new THREE.Vector2(opts.normalScale ?? 1, opts.normalScale ?? 1),
    aoMapIntensity: maps.aoMap ? 0.85 : 0,
    emissive: opts.emissive ?? 0x000000,
    emissiveIntensity: opts.emissiveIntensity ?? 0,
  });
}

/** Remap BoxGeometry face UVs so textures tile in world meters. */
export function setBoxWorldUVs(
  geometry: THREE.BoxGeometry,
  sx: number,
  sy: number,
  sz: number,
  metersPerTile = 2,
): void {
  const uv = geometry.attributes.uv as THREE.BufferAttribute;
  if (!uv || uv.count < 24) return;

  // Three.js BoxGeometry face order: +X -X +Y -Y +Z -Z (4 verts each)
  const faceUVSize: [number, number][] = [
    [sz, sy],
    [sz, sy],
    [sx, sz],
    [sx, sz],
    [sx, sy],
    [sx, sy],
  ];

  for (let f = 0; f < 6; f++) {
    const uScale = faceUVSize[f][0] / metersPerTile;
    const vScale = faceUVSize[f][1] / metersPerTile;
    for (let i = 0; i < 4; i++) {
      const idx = f * 4 + i;
      const u = idx % 2 === 0 || idx % 4 === 3 ? 0 : 1;
      const v = idx % 4 < 2 ? 0 : 1;
      // Preserve original corner pattern from geometry
      const ou = uv.getX(idx);
      const ov = uv.getY(idx);
      uv.setXY(idx, ou * uScale, ov * vScale);
      void u;
      void v;
    }
  }
  uv.needsUpdate = true;

  // aoMap uses uv2 in three.js by default
  if (!geometry.getAttribute('uv2')) {
    geometry.setAttribute('uv2', uv.clone());
  }
}

export function setPlaneWorldUVs(
  geometry: THREE.PlaneGeometry,
  width: number,
  height: number,
  metersPerTile = 2,
): void {
  const uv = geometry.attributes.uv as THREE.BufferAttribute;
  const uScale = width / metersPerTile;
  const vScale = height / metersPerTile;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) * uScale, uv.getY(i) * vScale);
  }
  uv.needsUpdate = true;
  if (!geometry.getAttribute('uv2')) {
    geometry.setAttribute('uv2', uv.clone());
  }
}

export type KeepMaterials = {
  wall: THREE.MeshStandardMaterial;
  wallDark: THREE.MeshStandardMaterial;
  wallWarm: THREE.MeshStandardMaterial;
  paving: THREE.MeshStandardMaterial;
  ground: THREE.MeshStandardMaterial;
  moss: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
  metalWarm: THREE.MeshStandardMaterial;
};

export function createKeepMaterials(): KeepMaterials {
  const rock = loadPbr(assetUrl('textures/stone'), 'rock');
  const paving = loadPbr(assetUrl('textures/stone'), 'paving');
  const dirt = loadPbr(assetUrl('textures/ground'), 'dirt');
  const moss = loadPbr(assetUrl('textures/moss'), 'moss');
  const wood = loadPbr(assetUrl('textures/wood'), 'planks');
  const iron = loadPbr(assetUrl('textures/metal'), 'iron');

  return {
    wall: makePbrMaterial(rock, {
      color: 0xb5a894,
      roughness: 0.95,
      normalScale: 0.9,
    }),
    wallDark: makePbrMaterial(rock, {
      color: 0x7a7062,
      roughness: 0.97,
      normalScale: 0.75,
    }),
    wallWarm: makePbrMaterial(rock, {
      color: 0xc4b49a,
      roughness: 0.93,
      normalScale: 0.95,
    }),
    paving: makePbrMaterial(paving, {
      color: 0xc8bba8,
      roughness: 0.92,
      normalScale: 1.05,
    }),
    ground: makePbrMaterial(dirt, {
      color: 0x9e8e76,
      roughness: 0.98,
      normalScale: 0.8,
    }),
    moss: makePbrMaterial(moss, {
      color: 0x7a8f5c,
      roughness: 0.98,
      normalScale: 1.05,
      repeat: 1.4,
    }),
    wood: makePbrMaterial(wood, {
      color: 0xa88862,
      roughness: 0.9,
      normalScale: 0.7,
    }),
    metal: makePbrMaterial(iron, {
      color: 0x5a534c,
      roughness: 0.72,
      metalness: 0.48,
      normalScale: 0.55,
    }),
    metalWarm: makePbrMaterial(iron, {
      color: 0x9a7a4e,
      roughness: 0.62,
      metalness: 0.42,
      normalScale: 0.45,
      emissive: 0x2a1806,
      emissiveIntensity: 0.12,
    }),
  };
}
